import { action, observable } from "mobx";
import { BULK_TASKS_OPERATION_DATE, IMPORT_CSV_DATE } from "Static/constant";
import makeApi, { URLS } from "Utils/apiURL";

export const JOB_FILTER = {
  ALL: "All",
  BULK_ACTIONS: "Bulk Actions",
  CSV_UPLOAD: "CSV Upload",
  // LINKEDIN_IMPORT: "LinkedIn Import",
  ENRICHMENT: "Enrichment"
};

export const BULK_NOTIFICATION_OPEN_STATUSES = ["TO_BE_PROCESSED", "IN_PROGRESS"];

const FETCH_LIMIT = 100;


const normalizeCsvImport = (item = {}) => ({
  id: item?.id,
  type: "CSV_UPLOAD",
  status: item?.status === "INPROGRESS" ? "IN_PROGRESS" : (item?.status || ""),
  createTs: item?.createTs,
  totalCount: item?.totalCount || 0,
  successCount: item?.successCount || 0,
  totalContacts: item?.totalContacts || 0,
  processedContacts: item?.processedContacts || 0
});

const normalizeLinkedInImport = (item = {}) => {
  const STATUS_MAP = {
    COMPLETED: "PROCESSED",
    ACTIVE: "IN_PROGRESS",
    PENDING: "TO_BE_PROCESSED",
    STOPPED: "TO_BE_PROCESSED",
    PAUSED: "TO_BE_PROCESSED"
  };
  return {
    id: item?.id,
    type: "LINKEDIN_EXTRACT",
    status: STATUS_MAP[item?.status] || item?.status || "",
    createTs: item?.createTs,
    totalCount: 0,
    successCount: item?.noOfContactsImported || 0,
    totalContacts: item?.totalContacts || 0,
    processedContacts: item?.noOfContactsImported || 0
  };
};

const normalizeExport = (item = {}) => ({
  id: item?.id,
  type: "DATA_EXPORT",
  status: item?.status === "PROCESSING" ? "IN_PROGRESS" : (item?.status || ""),
  createTs: item?.createTs,
  totalCount: 0,
  successCount: item?.processedContacts || 0,
  totalContacts: item?.totalContacts || 0,
  processedContacts: item?.processedContacts || 0
});


const FILTER_INVOKERS = {
  [JOB_FILTER.ALL]: async () => {
    const [bulk, csv, li, exports] = await Promise.all([
      makeApi({ url: URLS.tasksOperationLogs, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1, date: BULK_TASKS_OPERATION_DATE } }),
      makeApi({ url: URLS.csvImportsList, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1, date: IMPORT_CSV_DATE } }),
      makeApi({ url: URLS.linkedInImportsList, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1 } }),
      makeApi({ url: URLS.exports, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1 } })
    ]);
    return [
      ...(bulk?.data?.jobs || []),
      ...(csv?.data?.importList || []).map(normalizeCsvImport),
      ...(li?.data?.jobs || []).map(normalizeLinkedInImport),
      ...(exports?.data?.jobs || []).map(normalizeExport)
    ];
  },
  [JOB_FILTER.BULK_ACTIONS]: async () => {
    const res = await makeApi({ url: URLS.tasksOperationLogs, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1, date: BULK_TASKS_OPERATION_DATE } });
    return (res?.data?.jobs || []).filter(j => j?.type !== "ENRICH_CONTACT");
  },
  [JOB_FILTER.CSV_UPLOAD]: async () => {
    const res = await makeApi({ url: URLS.csvImportsList, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1, date: IMPORT_CSV_DATE } });
    return (res?.data?.importList || []).map(normalizeCsvImport);
  },
  [JOB_FILTER.LINKEDIN_IMPORT]: async () => {
    const res = await makeApi({ url: URLS.linkedInImportsList, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1 } });
    return (res?.data?.jobs || []).map(normalizeLinkedInImport);
  },
  [JOB_FILTER.ENRICHMENT]: async () => {
    const res = await makeApi({ url: URLS.tasksOperationLogs, method: "POST", data: { limit: FETCH_LIMIT, pageNo: 1, date: BULK_TASKS_OPERATION_DATE, type: "ENRICH_CONTACT" } });
    return res?.data?.jobs || [];
  }
};


const sortByCreateTs = (jobs = []) =>
  [...jobs].sort((a, b) => {
    const tsA = a?.createTs ? new Date(a.createTs).getTime() : 0;
    const tsB = b?.createTs ? new Date(b.createTs).getTime() : 0;
    return tsB - tsA;
  });


class BulkJobsStore {
  @observable jobs = [];
  @observable openJobsCount = 0;
  @observable loading = false;
  @observable guideVisible = false;
  @observable highlightActive = false;
  @observable guideMessage = "Track your bulk jobs here";

  _requestId = 0;
  guideTimer = null;
  highlightTimer = null;

  @action setJobs = (jobs = []) => {
    const sorted = sortByCreateTs(jobs);
    this.jobs = sorted;
    this.openJobsCount = sorted.filter(j => BULK_NOTIFICATION_OPEN_STATUSES.includes(j?.status)).length;
  };

  @action setLoading = (v = false) => { this.loading = v; };
  @action setGuideVisible = (v = false) => { this.guideVisible = v; };
  @action setHighlightActive = (v = false) => { this.highlightActive = v; };

  @action dismissGuide = () => {
    clearTimeout(this.guideTimer);
    clearTimeout(this.highlightTimer);
    this.guideTimer = this.highlightTimer = null;
    this.setGuideVisible(false);
    this.setHighlightActive(false);
  };

  @action triggerGuide = (message = "Track your bulk jobs here") => {
    this.dismissGuide();
    this.guideMessage = message;
    this.setGuideVisible(true);
    this.setHighlightActive(true);
    this.highlightTimer = setTimeout(() => this.setHighlightActive(false), 4500);
    this.guideTimer = setTimeout(() => this.setGuideVisible(false), 6500);
  };

  fetchJobs = () => this.fetch(JOB_FILTER.ALL);

  fetch = async (filter = JOB_FILTER.ALL) => {
    const invoker = FILTER_INVOKERS[filter];
    if (!invoker) return;

    const reqId = ++this._requestId;
    try {
      this.setLoading(true);
      const jobs = await invoker();
      if (reqId !== this._requestId) return;
      this.setJobs(jobs);
    } catch (e) {
      console.error(`BulkJobsStore.fetch(${filter}) failed`, e);
    } finally {
      if (reqId === this._requestId) this.setLoading(false);
    }
  };
}

const bulkJobsStore = new BulkJobsStore();
export default bulkJobsStore;
